'use client';

const MAGIC_LINK_CONTEXT_PREFIX = 'pz_auth_magic_link:';
const MAGIC_LINK_CONTEXT_TTL_MS = 30 * 60 * 1000;

interface MagicLinkContextRecord {
  requestedAt: number;
}

function contextKey(interactionToken: string): string {
  return `${MAGIC_LINK_CONTEXT_PREFIX}${interactionToken}`;
}

function storage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    // Intentionally session-scoped: opening the email link in another tab/window degrades to
    // cross-device approval while the original requester polls and completes.
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function markMagicLinkRequestContext(interactionToken: string): void {
  const sessionStorage = storage();
  if (!sessionStorage) {
    return;
  }

  const record: MagicLinkContextRecord = { requestedAt: Date.now() };
  sessionStorage.setItem(contextKey(interactionToken), JSON.stringify(record));
}

export function hasMagicLinkRequestContext(interactionToken: string): boolean {
  const sessionStorage = storage();
  if (!sessionStorage) {
    return false;
  }

  const rawRecord = sessionStorage.getItem(contextKey(interactionToken));
  if (!rawRecord) {
    return false;
  }

  try {
    const record = JSON.parse(rawRecord) as Partial<MagicLinkContextRecord>;
    if (typeof record.requestedAt !== 'number') {
      sessionStorage.removeItem(contextKey(interactionToken));
      return false;
    }

    if (Date.now() - record.requestedAt > MAGIC_LINK_CONTEXT_TTL_MS) {
      sessionStorage.removeItem(contextKey(interactionToken));
      return false;
    }

    return true;
  } catch {
    sessionStorage.removeItem(contextKey(interactionToken));
    return false;
  }
}

export function clearMagicLinkRequestContext(interactionToken: string): void {
  const sessionStorage = storage();
  if (!sessionStorage) {
    return;
  }

  sessionStorage.removeItem(contextKey(interactionToken));
}
